import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, merge, Subject } from 'rxjs';
import { UserAccountType } from 'src/app/data-types/user-data';
import { PaginationParams } from 'src/app/models/pagination-params';
import { LeadService } from '../lead.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ILeadListFiltersOutput } from './lead-list-filters/lead-list-filters.component';
import { UserService } from 'src/app/services/user.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AlertConfirmable } from 'src/app/decorators/alert-confirmable.decorator';
import { TwilioService } from 'src/app/services/twilio.service';
import { VoiceContactData } from 'src/app/data-types/voice-data';
import { ILead } from '../lead.interface';
import { AlertService } from 'src/app/services/alert.service';
import { MatDialog } from '@angular/material/dialog';
import { ILeadFormDlgResult, LeadFormComponent, LeadFormDlgDataParams } from '../lead-form/lead-form.component';
import { Store } from 'src/app/store';

export enum ECustomerType {
    Unknown = 'Unknown',
    Private = 'Private',
    Trade = 'Trade',
}

interface ILeadReqestParams extends PaginationParams {
    searchTerm: string;
    customerType: UserAccountType;
    leadType: 0 | 1;
    admin: string;
    startDate: string;
    endDate: string;
    ascOrDesc: 'ASC' | 'DESC';
}

export interface ILeadItem {
    name: string;
    firstname: string;
    surname: string;
    phone: string;
    email: string;
    customerType: string;
    managerId: string;
    managerName: string;
    createdAt: Date;
}

@UntilDestroy()
@Component({
    selector: 'app-leads-main',
    templateUrl: './leads-main.component.html',
    styleUrls: ['./leads-main.component.scss'],
})
export class LeadsMainComponent implements OnInit {
    selectedCustomerType: UserAccountType = null;
    selectedLeadType = null;

    displayedColumns: string[] = [
        'name',
        'phone',
        'email',
        'accountType',
        'leadType',
        'salesManager',
        'createdAt',
        'options',
    ];

    pParams: ILeadReqestParams = {
        limit: 10,
        limits: [10, 25, 50, 75, 100],
        page: 0,
        pages: 0,
        pageArray: [],
        totalRecords: 0,
        searchTerm: '',
        customerType: null,
        leadType: null,
        admin: null,
        startDate: null,
        endDate: null,
        sort: 'createdAt',
        ascOrDesc: 'DESC',
    } as ILeadReqestParams;

    showFilters: boolean = false;
    leadsDate = [];
    admins: [];
    searchTerm = new FormControl();
    isLoading: boolean = false;

    constructor(
        public twilioService: TwilioService,
        private leadService: LeadService,
        private userService: UserService,
        private router: Router,
        private toastrService: ToastrService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private store: Store
    ) {}

    get leads() {
        return this.leadsDate.slice(0, this.pParams.limit);
    }

    ngOnInit(): void {
        this.getLeads();
        this.userService.getAdmins().subscribe((data) => (this.admins = data.data));
        merge(this.searchTerm.valueChanges)
            .pipe(untilDestroyed(this))
            .pipe(debounceTime(300))
            .subscribe((val) => {
                this.pParams.searchTerm = val.trim();
                this.resetPage();
                this.getLeads();
            });

        this.getStateFromStore();
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
        this.saveStateIntoStore();
    }

    updatePaginator(totalRecords) {
        this.pParams.pages = Math.ceil(totalRecords / this.pParams.limit);
        this.pParams.totalRecords = totalRecords;
        this.pParams.pageArray = [];
        for (let i = 0; i < this.pParams.pages; i++) {
            this.pParams.pageArray.push(i);
        }
    }

    getLeads() {
        this.isLoading = true;

        this.leadService.getLeads(this.pParams).subscribe((leadsData) => {
            this.isLoading = false;
            this.updatePaginator(leadsData.totalRecords);
            this.leadsDate = leadsData.leads;
        });
    }

    onFiltersChanged(filterValues: ILeadListFiltersOutput) {
        this.resetPage();
        this.pParams.customerType = filterValues.customerType;
        this.pParams.leadType = filterValues.leadType;
        this.pParams.admin = filterValues.admin;
        this.pParams.startDate = filterValues.startDate;
        this.pParams.endDate = filterValues.endDate;
        this.getLeads();
    }

    selectLead(leadId: string) {
        this.router.navigate(['leads', leadId]);
    }

    sortData(sort) {
        this.pParams.sort = sort.active;
        this.pParams.ascOrDesc = sort.direction;
        this.resetPage();
        this.getLeads();
    }

    leadAdded(lead) {
        this.resetPage();
        this.getLeads();
        this.toastrService.success(`New lead ${lead.contact.firstname} ${lead.contact.surname} created`);
    }

    @AlertConfirmable({ title: 'Do you want to make a call?' })
    makeACallProspect(lead: ILead) {
        if (!this.twilioService.isLineBusy) {
            const contact: VoiceContactData = {
                id: lead.id,
                accountType: lead.accountType,
                contactId: lead.id,
                contactFirstname: lead.firstname,
                contactSurname: lead.surname,
                contactMobile: lead.phone,
                contactEmail: lead.email,
            } as VoiceContactData;
            this.twilioService.startOutboundCall(lead.phone, contact.id ? contact : null);
        } else {
            this.alertService.error([
                'Line is busy, please nicely finish ongoing conversation before starting the new one.',
            ]);
        }
    }

    @AlertConfirmable({
        title: 'Lead Archive',
        message: 'You sure you want to move this lead to Archive?',
        cancelText: 'Cancel',
    })
    async archiveLead(lead) {
        await this.leadService.archiveLead(lead);
        this.toastrService.success('Lead moved to archive');
        this.getLeads();
    }

    @AlertConfirmable({
        title: 'Lead On Hold',
        message: 'You sure you want to place this lead to on hold?',
        cancelText: 'Cancel',
    })
    async placeLeadOnHold(lead) {
        await this.leadService.placeLeadOnHold(lead);
        lead.onHold = 1;
        this.toastrService.success('Lead placed on hold');
    }

    createOrder(lead) {
        const url = this.router.createUrlTree(['/order', 'create', 'customer-select'], {
            queryParams: {
                select_customer: lead.id,
            },
        });
        window.open(url.toString(), '_blank');
    }

    openLeadForm() {
        this.dialog
            .open(LeadFormComponent, {
                ...LeadFormDlgDataParams,
            })
            .afterClosed()
            .subscribe((data: ILeadFormDlgResult) => {
                if (data?.status === true) {
                    this.leadAdded(data.lead);
                }
            });
    }

    private resetPage() {
        this.pParams.page = 0;
        this.pParams.totalRecords = null;
    }

    private getStateFromStore() {
        const leadsFiltersState = this.store.selectForLocal('leadsFilters');
        if (leadsFiltersState) {
            this.showFilters = leadsFiltersState.showFilters;
        }
    }

    private saveStateIntoStore() {
        const leadsFiltersState = this.store.selectForLocal('leadsFilters');
        leadsFiltersState.showFilters = this.showFilters;
        this.store.set('leadsFilters', leadsFiltersState);
    }
}
